/**
 * TenderWorkspace — single-page home for one tender (formerly known as an RFP).
 *
 * This is the Great Simplification's anchor route (see BACKLOG.md). It wraps
 * the existing v1 page components as tabs so we get the consolidated mental
 * model immediately, without rewriting them. The v1 pages each consume their
 * own routes/endpoints today; we feed them the rfpId via URL params so they
 * keep working unchanged. Subsequent passes port them to v2 endpoints and
 * inline-fold them.
 *
 * Tab order matches the user's mental model:
 *   Brief · Eligibility · BoM · Quotation · Proposal & Cover · Outcome
 *
 * Plus a slide-over SearchPanel triggered by Cmd/Ctrl-K or the header
 * Search button. Hits with a known section_index switch the tab to
 * Brief and focus that section; the panel itself stays open so users
 * can keep reading hits in research mode.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  FileText, ShieldCheck, ListTree, Receipt, Trophy, ArrowLeft, Search,
  RefreshCcw, Loader2, FileSearch, ListChecks, Target, ClipboardCheck,
  Paperclip, Sparkles, Trash2, Plus, MessageCircle,
  Briefcase, Building2, CheckCircle2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import RFPAnalysis from "@/pages/RFPAnalysis";
import ProposalGenerator from "@/pages/ProposalGenerator";
import QuotationPanel, { GenerateDialog } from "@/components/v2/QuotationPanel";
import BidBoqEditor from "@/components/v2/BidBoqEditor";
import BoqGrid from "@/components/v2/BoqGrid";
import OutcomeCaptureForm from "@/components/v2/OutcomeCaptureForm";
import EligibilityPanel from "@/components/v2/EligibilityPanel";

import BomPanel from "@/components/v2/BomPanel";
import BidConfidencePanel from "@/components/v2/BidConfidencePanel";
import LibraryExemplarsPanel from "@/components/v2/LibraryExemplarsPanel";
import SearchPanel from "@/components/v2/SearchPanel";
import OcrReviewPanel from "@/components/v2/OcrReviewPanel";
import AnalyseProgressBanner from "@/components/v2/AnalyseProgressBanner";
import BidSummaryCard from "@/components/v2/BidSummaryCard";
import SubmissionChecklistPanel from "@/components/v2/SubmissionChecklistPanel";
import RfpChatPanel from "@/components/v2/RfpChatPanel";
import { v2Api } from "@/lib/v2Api";
import { useAuth } from "@/contexts/AuthContext";
import { RfpProvider, useRfp } from "@/contexts/RfpContext";


const VALID_TABS = [
  "brief", "pins", "checklist", "ai", "workflow",
  "quotation", "outcome", "ask",
  // Legacy aliases kept resolvable so bookmarked /tender/:id?tab=matrix /
  // ?tab=proposal / ?tab=eligibility URLs still route under the new AI
  // umbrella via LEGACY_TAB_ALIAS below.
  "matrix", "proposal", "eligibility",
];
// Old ?tab=bom and ?tab=boq URLs redirect to Brief (BOQ now lives under Brief > BOQ tab).
// v3 UX consolidated Bid Matrix + Eligibility + Proposal under the AI tab.
const LEGACY_TAB_ALIAS = {
  bom: "brief",
  boq: "brief",
  matrix: "ai",
  eligibility: "ai",
  proposal: "ai",
};
const DEFAULT_TAB = "brief";


/**
 * ChannelNotificationPref — per-channel notification preference.
 *
 * Reads + writes channel_member.notif_pref via the PR 5 endpoints, so
 * the backend mention fan-out can actually skip notifications for
 * muted users. Falls back to localStorage if the API call fails (e.g.
 * not authenticated yet, network blip) so the dropdown stays usable.
 */
function ChannelNotificationPref({ rfpId }) {
  const { user } = useAuth();
  const LEGACY_KEY = `v3.channelNotif.${rfpId}`;
  const [pref, setPref] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initial load: prefer server, fall back to legacy localStorage.
  useEffect(() => {
    if (!rfpId || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const members = await v2Api.channels.listMembers(rfpId);
        const me = (members || []).find((m) => m.user_id === user.id);
        if (!cancelled && me?.notif_pref) {
          setPref(me.notif_pref);
          return;
        }
      } catch { /* fall through to localStorage */ }
      try {
        const legacy = localStorage.getItem(LEGACY_KEY);
        if (!cancelled && legacy) setPref(legacy);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [rfpId, user?.id, LEGACY_KEY]);

  const set = async (next) => {
    setPref(next);  // optimistic
    setOpen(false);
    if (!user?.id) return;
    setSaving(true);
    try {
      await v2Api.channels.setMemberPref(rfpId, user.id, { notif_pref: next });
      // Mirror to localStorage so a logged-out reload still shows the
      // user's last choice on the next login.
      try { localStorage.setItem(LEGACY_KEY, next); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save preference");
    } finally {
      setSaving(false);
    }
  };
  const label = pref === "muted" ? "Muted" : pref === "mentions" ? "Mentions only" : "All";
  const icon = pref === "muted" ? "🔕" : pref === "mentions" ? "🔔" : "🔔";
  return (
    <div className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 text-[11px] rounded-md px-2 py-1 border transition ${
          pref === "muted"
            ? "border-slate-300 text-slate-500 bg-slate-50"
            : "border-slate-200 text-slate-600 hover:bg-slate-50"
        }`}
        title="Per-channel notifications"
      >
        <span>{icon}</span> {label}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-xl z-[60] py-1 text-sm"
          onMouseLeave={() => setOpen(false)}
        >
          {[
            { v: "all", label: "All messages" },
            { v: "mentions", label: "@-mentions only" },
            { v: "muted", label: "Mute this channel" },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => set(o.v)}
              className={`w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center justify-between ${
                pref === o.v ? "text-blue-600 font-medium" : "text-slate-700"
              }`}
            >
              {o.label}
              {pref === o.v && <span className="text-blue-600">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * ChannelMembers — compact members widget for the channel header.
 *
 * Stage 1 simple version: shows the workspace headcount (lead picker
 * users) with a popover-style summary. Stage 2 introduces per-channel
 * membership via a channel_member table + invite flow.
 */
function ChannelMembers({ rfp }) {
  const [count, setCount] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rfpId = rfp?.id;

  const refresh = useCallback(async () => {
    if (!rfpId) return;
    try {
      const { v2Api } = await import("@/lib/v2Api");
      const rows = await v2Api.channels.listMembers(rfpId);
      const list = Array.isArray(rows) ? rows : [];
      setMembers(list);
      setCount(list.length);
    } catch (e) {
      setCount(0);
    }
  }, [rfpId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load all workspace users only when the panel opens — needed for the
  // "add member" dropdown.
  useEffect(() => {
    if (!open || allUsers.length) return;
    import("@/lib/v2Api").then(({ v2Api }) => {
      v2Api.users()
        .then((u) => setAllUsers((u || []).filter((x) => x.is_active !== false)))
        .catch(() => {});
    });
  }, [open, allUsers.length]);

  const removeMember = async (userId) => {
    setBusy(true);
    try {
      const { v2Api } = await import("@/lib/v2Api");
      await v2Api.channels.removeMember(rfpId, userId);
      setMembers((prev) => prev.filter((m) => m.user_id !== userId));
      setCount((c) => Math.max(0, (c || 1) - 1));
    } catch (e) { /* silent */ } finally { setBusy(false); }
  };

  const addMember = async (userId) => {
    setBusy(true);
    try {
      const { v2Api } = await import("@/lib/v2Api");
      const created = await v2Api.channels.addMember(rfpId, userId);
      setMembers((prev) => [...prev.filter((m) => m.user_id !== userId), created]);
      setCount((c) => (c || 0) + 1);
    } catch (e) { /* silent */ } finally { setBusy(false); }
  };

  if (count == null) return null;

  const memberIds = new Set(members.map((m) => m.user_id));
  const addable = allUsers.filter((u) => !memberIds.has(u.id));
  const initialsList = members.slice(0, 3).map((m) =>
    (m.name || m.email || "?").split(/\s|@/).filter(Boolean).slice(0, 2)
      .map((s) => s[0].toUpperCase()).join("")
  );
  return (
    <div className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-slate-900 rounded-md px-1.5 py-0.5 hover:bg-slate-100"
        title={`${count} member${count === 1 ? "" : "s"} — click for list`}
      >
        <div className="flex -space-x-1.5">
          {initialsList.map((ini, i) => (
            <span
              key={i}
              className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white text-[9px] font-semibold flex items-center justify-center ring-1 ring-white"
            >{ini}</span>
          ))}
        </div>
        <span>{count}</span>
      </button>
      {open && (
        <div
          // Open BELOW the trigger and right-align to it so the panel stays
          // inside the channel main pane and never bleeds into the dark
          // sidebar. z-[60] beats the sidebar (z-40) so the popover paints
          // on top instead of being hidden behind it.
          className="fixed sm:absolute right-2 sm:right-0 top-12 sm:top-full sm:mt-1 w-72 max-w-[calc(100vw-2rem)] max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl z-[60] p-2"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Channel members</span>
            <span className="text-slate-300">{count}</span>
          </div>
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-[12px] group/row">
              <span className="w-5 h-5 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-white text-[9px] font-semibold flex items-center justify-center">
                {(m.name || m.email || "?").split(/\s|@/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("")}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 truncate">{m.name || m.email}</div>
                <div className="text-[10px] text-slate-400 truncate">{m.email}</div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => removeMember(m.user_id)}
                className="opacity-0 group-hover/row:opacity-100 text-[10px] text-rose-500 hover:text-rose-700"
                title="Remove from channel"
              >×</button>
            </div>
          ))}
          {addable.length > 0 && (
            <div className="border-t border-slate-100 mt-1 pt-1">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Add member
              </div>
              {addable.slice(0, 8).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  disabled={busy}
                  onClick={() => addMember(u.id)}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-blue-50 text-[12px]"
                >
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-semibold flex items-center justify-center">+</span>
                  <span className="flex-1 truncate">{u.name || u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * QuotationTab — non-blocking quotation entry for the Tender workspace.
 *
 * Earlier wiring auto-fetched /projects/{id}/bids on tab mount and
 * handed the row to QuotationPanel. On at least one tender that path
 * locked the browser ("unresponsive page") — either the bid lookup
 * hung or QuotationPanel had a latent bug now that it's actually
 * mounted. Until we can isolate it, the tab no longer auto-fetches.
 * It shows a stable landing page with two paths:
 *   1. Download the priced BoQ Excel (already works, server-rendered).
 *   2. Opt in to load the live QuotationPanel — only on explicit click,
 *      so a misbehaving fetch can never block the page on first render.
 */
const DECISION_CLS = {
  go: "bg-emerald-100 text-emerald-800",
  no_go: "bg-rose-100 text-rose-800",
  undecided: "bg-slate-100 text-slate-600",
};
const STAGE_LABEL = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

function QuotationTab({ rfp }) {
  const { user } = useAuth();
  const [bid, setBid] = useState(null);
  const [bidLoading, setBidLoading] = useState(false);
  const [bidError, setBidError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingMode, setCreatingMode] = useState(null); // "rfp" | "empty"
  const [boqRefreshKey, setBoqRefreshKey] = useState(0);
  const [quotRefreshKey, setQuotRefreshKey] = useState(0);
  const [bidSubTab, setBidSubTab] = useState("boq");
  const [boqSummary, setBoqSummary] = useState(null); // {total, matched, withSuggestedRate, priced}
  const [genDialogOpen, setGenDialogOpen] = useState(false);

  const projectId = rfp?.project_id;
  const rfpId = rfp?.id;

  const loadBid = useCallback(async () => {
    if (!projectId || !rfpId) return;
    setBidLoading(true);
    setBidError(null);
    try {
      const bids = await v2Api.projects.bids(projectId);
      const match = (Array.isArray(bids) ? bids : []).find(
        (b) => String(b.rfp_id) === String(rfpId),
      );
      setBid(match || null);
    } catch (e) {
      setBidError(e?.response?.data?.detail || e?.message || "Bid lookup failed");
    } finally {
      setBidLoading(false);
    }
  }, [projectId, rfpId]);

  useEffect(() => { loadBid(); }, [loadBid]);

  // Load BOQ summary (matched count, pricing progress) whenever bid or refresh key changes
  useEffect(() => {
    if (!bid?.id) { setBoqSummary(null); return; }
    v2Api.boq.list(bid.id).then((rows) => {
      const active = rows.filter((r) => !r.is_deleted);
      setBoqSummary({
        total: active.length,
        matched: active.filter((r) => !!r.matched_sku_id).length,
        withSuggestedRate: active.filter((r) => r.suggested_unit_rate != null).length,
        priced: active.filter((r) => Number(r.unit_rate || 0) > 0).length,
      });
    }).catch(() => setBoqSummary(null));
  }, [bid?.id, boqRefreshKey]);

  const createBid = async (syncFromRfp) => {
    if (!projectId || !rfpId) { toast.error("Missing project or RFP — refresh the page."); return; }
    setCreating(true);
    setCreatingMode(syncFromRfp ? "rfp" : "empty");
    setBidError(null);
    try {
      const created = await v2Api.bids.create({
        project_id: projectId,
        rfp_id: rfpId,
        name: (rfp?.title || rfp?.name || "Bid for RFP").slice(0, 240),
        submitted_value_currency: "INR",
      });
      setBid(created);
      if (syncFromRfp) {
        try {
          const lines = await v2Api.boq.syncFromRfp(created.id);
          setBoqRefreshKey((k) => k + 1);
          toast.success(`Bid created · ${lines?.length || 0} lines imported from RFP`);
        } catch (syncErr) {
          const detail = syncErr?.response?.data?.detail || syncErr?.message;
          toast.info(`Bid created — BOQ not auto-synced: ${detail || "use Re-sync from RFP"}`);
        }
      } else {
        toast.success("Bid created — add items manually or import from Excel");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : (e?.message || "Could not create bid"));
      setBidError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
      setCreatingMode(null);
    }
  };

  if (bidLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bid…
      </div>
    );
  }

  if (bidError) {
    return (
      <div className="p-8 space-y-2">
        <p className="text-sm text-rose-700">{bidError}</p>
        <Button size="sm" variant="outline" onClick={loadBid}>Retry</Button>
      </div>
    );
  }

  /* ── No bid yet ── */
  if (!bid) {
    return (
      <div className="p-8 max-w-md space-y-4">
        <div>
          <h3 className="font-semibold text-slate-800 text-base flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-blue-600" /> No bid yet for this tender
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Creates a bid and auto-populates BOQ lines from the RFP analysis.
            You can edit, add items, or update pricing after.
          </p>
        </div>
        <Button
          disabled={creating}
          onClick={() => createBid(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {creating
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
            : <><Plus className="w-4 h-4 mr-2" /> Create bid &amp; populate BOQ</>}
        </Button>
        {bidError && <p className="text-sm text-rose-600">{bidError}</p>}
      </div>
    );
  }

  /* ── Bid exists — BOQ + Quotation sub-tabs ── */
  return (
    <div className="p-4 space-y-4">
      {/* Bid header bar */}
      <div className="flex items-center justify-between gap-3 border rounded-lg p-3 bg-slate-50/60">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
            <Briefcase className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800 truncate">{bid.name}</span>
              <Badge className={`text-xs shrink-0 ${DECISION_CLS[bid.decision] || DECISION_CLS.undecided}`}>
                {bid.decision === "go" ? "Go" : bid.decision === "no_go" ? "No-go" : "Undecided"}
              </Badge>
              <span className="text-xs text-slate-400">{STAGE_LABEL(bid.stage)}</span>
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setBoqRefreshKey((k) => k + 1)}
          title="Reload pricing lines from the BOQ tab — excludes soft-deleted items"
        >
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Import from BOQ
        </Button>
      </div>

      {/* Sub-tabs */}
      <Tabs value={bidSubTab} onValueChange={setBidSubTab}>
        <TabsList className="bg-slate-100 h-9">
          <TabsTrigger value="boq" className="data-[state=active]:bg-white text-xs">
            <ListTree className="w-3.5 h-3.5 mr-1.5" /> BOQ &amp; Pricing
          </TabsTrigger>
          <TabsTrigger value="quotations" className="data-[state=active]:bg-white text-xs">
            <Receipt className="w-3.5 h-3.5 mr-1.5" /> Quotation Documents
          </TabsTrigger>
          <TabsTrigger value="proposal_gen" className="data-[state=active]:bg-white text-xs">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Proposal Generator
          </TabsTrigger>
        </TabsList>

        <TabsContent value="boq" className="mt-3 space-y-3">
          {/* BOQ summary from BOQ tab work */}
          {boqSummary && boqSummary.total > 0 && (
            <div className={`rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${
              boqSummary.matched > 0
                ? "border-emerald-100 bg-emerald-50 text-emerald-800"
                : "border-amber-100 bg-amber-50 text-amber-800"
            }`}>
              {boqSummary.matched > 0
                ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                : <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
              }
              <span>
                {boqSummary.matched > 0 ? (
                  <>
                    <strong>{boqSummary.matched} of {boqSummary.total} lines</strong> matched to catalogue by the BOQ tab.{" "}
                    {boqSummary.priced < boqSummary.withSuggestedRate && boqSummary.withSuggestedRate > 0 && (
                      <>Click <strong>Use ₹X</strong> on matched lines to pre-fill rates from catalogue prices.</>
                    )}
                    {boqSummary.priced > 0 && boqSummary.priced === boqSummary.total && (
                      <> All lines priced — generate a quotation below.</>
                    )}
                  </>
                ) : (
                  <><strong>{boqSummary.total} lines</strong> loaded. Go to the BOQ tab to match items to catalogue, then rates will appear here.</>
                )}
              </span>
            </div>
          )}
          <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
            Pricing is <strong>internal only</strong> — not shown to the buyer.
            Use <strong>Add manual item</strong> for scope not in the RFP, or{" "}
            <strong>Import buyer Excel</strong> to load the buyer's price schedule.
          </div>
          <BoqGrid
            bid={bid}
            project={{ id: projectId, currency: "INR" }}
            refreshBid={loadBid}
            rfpId={rfpId}
            mode="pricing"
            key={boqRefreshKey}
            onGenerateQuot={() => setGenDialogOpen(true)}
          />
          <GenerateDialog
            open={genDialogOpen}
            onOpenChange={setGenDialogOpen}
            bid={bid}
            onGenerated={() => {
              setGenDialogOpen(false);
              setQuotRefreshKey((k) => k + 1);
              setBidSubTab("quotations");
            }}
          />
        </TabsContent>

        <TabsContent value="quotations" className="mt-3">
          <QuotationPanel
            bid={bid}
            project={{ id: projectId }}
            refreshKey={quotRefreshKey}
            onGenerated={() => setQuotRefreshKey((k) => k + 1)}
          />
        </TabsContent>

        <TabsContent value="proposal_gen" className="mt-3 space-y-4">
          <LibraryExemplarsPanel rfpId={rfpId} />
          <ProposalGenerator rfpId={rfpId} embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function WorkspaceInner({ rfpId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { rfp, loading: rfpLoading, error: rfpError, focusToken, refresh, canManageRfp } = useRfp();
  // Only admins, managers, and the project lead can trigger re-analysis.
  const canReanalyse = canManageRfp;
  const [searchOpen, setSearchOpen] = useState(false);
  // Project metadata cache for the Outcome tab (needs client_name to
  // pre-fill the form). Fetched lazily; null when project not yet
  // resolved. Light payload — one GET per tender open.
  const [projectMeta, setProjectMeta] = useState(null);
  useEffect(() => {
    if (!rfp?.project_id) return;
    let cancelled = false;
    v2Api.projects.get(rfp.project_id)
      .then((p) => { if (!cancelled) setProjectMeta(p); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rfp?.project_id]);
  // OCR review panel — only the button render is gated on _ocr.ocr_page_count;
  // open state lives here so Cmd-Shift-O can also toggle it in a later commit.
  const [ocrOpen, setOcrOpen] = useState(false);
  const [confirmReanalyse, setConfirmReanalyse] = useState(false);
  const [reanalysing, setReanalysing] = useState(false);

  // Early return for dead URLs / stale tender ids — otherwise BidSummaryCard,
  // SearchPanel, and the legacy Brief tab all fire requests against a tender
  // that doesn't exist and surface them as scary "Network Error" toasts.
  // Placed AFTER all hooks so rules-of-hooks isn't violated.
  const _tenderMissing = !rfpLoading && (rfpError || !rfp);

  const parseStatus = (rfp?.parse_status || "").toUpperCase();
  const isParsing = parseStatus === "PARSING";

  const runReanalyse = useCallback(async () => {
    setConfirmReanalyse(false);
    setReanalysing(true);
    try {
      await v2Api.rfp.analyzeAgain(rfpId);
      toast.success("Re-analysed — refreshing tender");
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? "Re-analyse failed"
      );
    } finally {
      setReanalysing(false);
    }
  }, [rfpId, refresh]);

  // U1: tab lives in the URL (?tab=eligibility). Refresh + share + back-button
  // all preserve the active tab. Unknown values fall back to "brief".
  const rawTab = searchParams.get("tab");
  // Resolve legacy aliases (bom, boq → brief; matrix/eligibility/proposal → ai).
  // Anything unknown falls back to DEFAULT_TAB.
  const resolvedTab = LEGACY_TAB_ALIAS[rawTab] || rawTab;
  const tab = VALID_TABS.includes(resolvedTab) ? resolvedTab : DEFAULT_TAB;
  const setTab = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_TAB) {
      params.delete("tab");
    } else {
      params.set("tab", next);
    }
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);

  // Cmd/Ctrl-K opens the slide-over from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // SearchPanel hit click → focusSection() bumps focusToken. We listen
  // here so we can swap to the Brief tab whenever a new focus is set
  // (RFPAnalysis itself handles the scroll-into-view).
  useEffect(() => {
    if (focusToken > 0 && tab !== "brief") setTab("brief");
    // intentionally NOT depending on `tab` — we don't want to fight a
    // user who switches tabs mid-focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusToken]);

  if (_tenderMissing) {
    return (
      <div className="p-10 text-center text-slate-600" data-testid="tender-not-found">
        <p className="text-lg font-medium">This tender no longer exists.</p>
        <p className="mt-1 text-sm">It may have been deleted or re-seeded. Go back to the list.</p>
        <Button className="mt-4" onClick={() => navigate("/tenders")}>
          Back to Tenders
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Slack-style channel header — v3 UX Stage 1.
          Replaces the old "Tender workspace" h1 with a channel-style row:
            #channel-name · tender_ref · stage badge · ⭐ star · members count
          Star toggles localStorage v3.starredChannels (consumed by the new
          Sidebar to bucket this RFP under "Watching"). */}
      {/* pl-14 on mobile leaves room for the sidebar hamburger button. */}
      <div className="pl-14 pr-3 lg:px-6 py-3 border-b bg-white sticky top-0 z-10 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <button
              onClick={() => navigate("/tenders")}
              className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> All channels
            </button>
            {rfp?.project_id && (
              <button
                onClick={() => navigate(`/v2/projects/${rfp.project_id}`)}
                className="text-[10px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                title="Open the parent project"
              >
                · Project: {projectMeta?.name || projectMeta?.code || "View project"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <button
              type="button"
              onClick={() => {
                try {
                  const key = "v3.starredChannels";
                  const raw = localStorage.getItem(key);
                  const set = new Set(raw ? JSON.parse(raw) : []);
                  if (set.has(rfpId)) set.delete(rfpId); else set.add(rfpId);
                  localStorage.setItem(key, JSON.stringify(Array.from(set)));
                  // Force a sidebar re-render via custom event (no shared store).
                  window.dispatchEvent(new Event("v3.starredChannels.changed"));
                } catch { /* ignore */ }
              }}
              className="text-slate-300 hover:text-amber-500"
              title="Star this channel (appears under Watching in the sidebar)"
            >★</button>
            <span className="text-slate-400 font-mono text-lg">#</span>
            <h1 className="text-lg font-semibold text-slate-900 truncate" title={rfp?.title || rfp?.name}>
              {(rfp?.title || rfp?.name || "tender").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48)}
            </h1>
            {rfp?.rfp_ref && (
              <span className="text-[11px] text-slate-500 font-mono ml-2 truncate">{rfp.rfp_ref}</span>
            )}
            <ChannelMembers rfp={rfp} />
            <ChannelNotificationPref rfpId={rfpId} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {parseStatus && parseStatus !== "ANALYZED" && (
            <Badge
              className={
                parseStatus === "FAILED"
                  ? "bg-rose-100 text-rose-700"
                  : parseStatus === "PARSING"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-700"
              }
              title={rfp?.parse_error || ""}
            >
              {parseStatus}
            </Badge>
          )}
          <Button
            variant="outline" size="sm"
            onClick={() => setConfirmReanalyse(true)}
            disabled={reanalysing || isParsing || !rfp || !canReanalyse}
            className="gap-1.5"
            title={canReanalyse ? "Re-run analysis on this tender" : "Only project leads and department leads can re-analyse"}
          >
            {reanalysing
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCcw className="w-3.5 h-3.5" />}
            {reanalysing ? "Re-analysing…" : "Re-analyse"}
          </Button>
          {/* OCR review only renders when the parser used OCR on at least
              one page. Most digital PDFs hit zero — we hide the button so
              the header stays uncluttered. Reads off rfp.analysis._ocr
              (workspace summary written by analyze_rfp). */}
          {(rfp?.analysis?._ocr?.ocr_page_count ?? 0) > 0 && (
            <Button
              variant="outline" size="sm"
              onClick={() => setOcrOpen(true)}
              className="gap-1.5"
              title="Verify OCR'd regions against the original page"
            >
              <FileSearch className="w-3.5 h-3.5" />
              OCR review
              {(rfp?.analysis?._ocr?.needs_review_page_count ?? 0) > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold">
                  {rfp.analysis._ocr.needs_review_page_count}
                </span>
              )}
            </Button>
          )}
          {rfp && (rfp.file_name || rfp.file_path) && (
            <Button
              variant="outline" size="sm"
              onClick={() => v2Api.rfp.openFileInNewTab(rfpId).catch(() => {})}
              className="gap-1.5"
              title="Open the source RFP document"
            >
              <Paperclip className="w-3.5 h-3.5" /> View RFP
            </Button>
          )}
          <Button
            variant="outline" size="sm"
            onClick={() => setSearchOpen(true)}
            className="gap-1.5"
            title="Search this tender (⌘K / Ctrl-K)"
          >
            <Search className="w-3.5 h-3.5" /> Search
            <kbd className="ml-1 px-1.5 py-0.5 text-[10px] rounded border bg-slate-50 text-slate-500">
              ⌘K
            </kbd>
          </Button>
        </div>
      </div>

      {rfp?.analysis?._chunking_failed && (
        <div className="mx-4 mt-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2">
          <Search className="w-3.5 h-3.5 shrink-0" />
          <span>Search index failed to build for this tender — keyword and AI search won't return results. Re-analyse to fix.</span>
        </div>
      )}

      {/* Live progress banner — polls /rfp/{id}/progress every 2s
          while parse_status==PARSING, stops on terminal state.
          Hidden otherwise. On completion it refreshes the RfpContext
          so the rest of the page re-fetches the freshly-analysed
          payload without the user having to reload. */}
      {parseStatus === "PARSING" && (
        <div className="px-6 pb-3 -mt-2">
          <AnalyseProgressBanner
            rfpId={rfpId}
            parseStatus={parseStatus}
            onComplete={() => refresh()}
            onPartial={() => refresh()}
          />
        </div>
      )}

      <AlertDialog open={confirmReanalyse} onOpenChange={setConfirmReanalyse}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-analyse this tender?</AlertDialogTitle>
            <AlertDialogDescription>
              The full parse + classify + extract pipeline re-runs on the
              uploaded file. Existing sections, the executive summary, and
              field extractions are replaced. Manual reclassifications and
              comments are preserved. Typical runtime: 15-90 seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reanalysing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runReanalyse} disabled={reanalysing}>
              {reanalysing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Re-analyse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* BidSummaryCard moved into the Pins tab (v3 UX) — it's auto-pinned
          on channel creation and stays the system's first pin. Keeps Messages
          / Canvas tabs uncluttered by what is essentially a sidebar widget. */}

      <div className="p-4">
        <Tabs value={tab} onValueChange={setTab}>
          {/* BoM tab hidden until the BomPanel editor ships — it was a
              placeholder ("BoM editor coming soon") that confused users
              by sitting next to BoQ as if they were peers. Old ?tab=bom
              URLs still resolve to BoQ via LEGACY_TAB_ALIAS. */}
          {/* Tab strip — Canvas is the default tab. Messages moved to the
              persistent ChatSidebar on the right. */}
          {/* Flex strip so each tab takes its natural width. Horizontal
              scroll on overflow rather than squishing all 10+ labels into
              equal columns (which was running "Proposal & Cover" into
              "Outcome" in the v1 polish round). */}
          <TabsList className="flex flex-nowrap gap-1 overflow-x-auto justify-start h-auto w-full max-w-full p-1 bg-slate-100 scrollbar-thin">
            <TabsTrigger value="brief" className="shrink-0">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Canvas
            </TabsTrigger>

            <TabsTrigger value="checklist" className="shrink-0">
              <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Tasks
            </TabsTrigger>

            <TabsTrigger value="ai" className="shrink-0">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI
            </TabsTrigger>
            <TabsTrigger value="quotation" className="shrink-0">
              <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Bid
            </TabsTrigger>
            <TabsTrigger value="outcome" className="shrink-0">
              <Trophy className="w-3.5 h-3.5 mr-1.5" /> Outcome
            </TabsTrigger>
            <TabsTrigger value="ask" className="shrink-0">
              <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Ask RFP
            </TabsTrigger>
          </TabsList>

          {/* Brief — wraps the existing RFPAnalysis page (sections, summary, deadlines). */}
          <TabsContent value="brief">
            <RFPAnalysis />
          </TabsContent>


          {/* Checklist — submission artifact: every "submit X / Annex Y /
              declaration" detected in the tender, checkable + PDF export. */}
          <TabsContent value="checklist">
            <SubmissionChecklistPanel rfpId={rfpId} />
          </TabsContent>

          {/* BoQ — editable bill of quantities backed by the bid's BoqLine DB records. */}
          {/* AI — consolidates the three LLM-heavy surfaces (Eligibility,
              Bid Matrix readiness, Proposal generator) under one tab with
              nested sub-tabs. Drops the top strip from 11 → 10 and groups
              the AI work somewhere a user can mentally locate it. */}
          <TabsContent value="ai">
            <Tabs defaultValue="score" className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="border-b border-slate-100 bg-slate-50 px-2 pt-2">
                <TabsList className="bg-transparent gap-1">
                  <TabsTrigger value="score" className="data-[state=active]:bg-white">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Bid Confidence
                  </TabsTrigger>
                  <TabsTrigger value="eligibility" className="data-[state=active]:bg-white">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Eligibility
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="score" className="p-0">
                <BidConfidencePanel rfpId={rfpId} />
              </TabsContent>
              <TabsContent value="eligibility" className="p-0">
                <EligibilityPanel rfpId={rfpId} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Quotation — wraps QuotationPanel. */}
          <TabsContent value="quotation">
            <QuotationTab rfp={rfp} />
          </TabsContent>


          {/* Outcome — writes a HistoricalBid with the top-3 bidders array. */}
          <TabsContent value="outcome">
            <div className="p-8 max-w-3xl mx-auto space-y-3">
              <OutcomeCaptureForm
                rfpId={rfpId}
                bidName={rfp?.title || ""}
                clientName={projectMeta?.client_name || ""}
              />
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => navigate("/v2/historical")}>
                  See all captured outcomes →
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Ask RFP — multi-turn conversational chat scoped to this tender */}
          <TabsContent value="ask" className="mt-0">
            <div style={{ height: "calc(100vh - 200px)", minHeight: "500px" }}>
              <RfpChatPanel rfpId={rfpId} rfp={rfp} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <SearchPanel open={searchOpen} onOpenChange={setSearchOpen} />
      <OcrReviewPanel open={ocrOpen} onOpenChange={setOcrOpen} />
    </div>
  );
}


export default function TenderWorkspace() {
  const { rfpId } = useParams();
  return (
    <RfpProvider key={rfpId} rfpId={rfpId}>
      <WorkspaceInner rfpId={rfpId} />
    </RfpProvider>
  );
}
